import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Menu, Popover, Typography } from "antd";
import { Fragment, useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteCustomer } from "../../redux/actions/customer/deleteCustomerAction";
import { loadSingleCustomer } from "../../redux/actions/customer/detailCustomerAction";
import Loader from "../loader/loader";
import CustomerInvoiceList, { DEFAULT_CUSTOMER_INVOICE_COLUMNS } from "../Card/CustomerInvoiceList";
import CustomerReturnInvoiceList from "./ListCard/CustomerReturnInvoiceList";
import CustomerTransactionList from "./ListCard/CustomerTransactionList";

const TABLE_SCROLL_Y = 360;

const DetailCust = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customer = useSelector((state) => state.customers.customer);

  const buildColumns = () =>
    DEFAULT_CUSTOMER_INVOICE_COLUMNS.map((col) =>
      col.key === "id"
        ? { ...col, render: (id) => <Link to={`/sale/${id}`}>{id}</Link> }
        : col.key === "payment"
        ? {
            ...col,
            render: (id) => (
              <Link to={`/payment/customer/${id}`}>
                <Button type="primary" size="small">Payment</Button>
              </Link>
            ),
          }
        : col
    );
  const initialColumns = buildColumns();
  const [columnItems, setColumnItems] = useState(() =>
    initialColumns.map((item) => ({
      key: item.key,
      label: <span>{item.title}</span>,
    }))
  );
  const [columnsToShow, setColumnsToShow] = useState(initialColumns);

  useEffect(() => {
    const withRenders = buildColumns();
    setColumnsToShow(withRenders);
    setColumnItems(
      withRenders.map((item) => ({
        key: item.key,
        label: <span>{item.title}</span>,
      }))
    );
  }, []);

  const colVisibilityClickHandler = (col) => {
    const ifColFound = columnsToShow.find((item) => item.key === col.key);
    if (ifColFound) {
      setColumnsToShow((prev) => prev.filter((item) => item.key !== col.key));
    } else {
      const base = buildColumns();
      const foundIndex = base.findIndex((item) => item.key === col.key);
      const foundCol = base.find((item) => item.key === col.key);
      setColumnsToShow((prev) => {
        const next = [...prev];
        next.splice(foundIndex, 0, foundCol);
        return next;
      });
    }
  };

  const onDelete = () => {
    try {
      dispatch(deleteCustomer(id));
      setVisible(false);
      toast.warning(`Customer : ${customer.name} is removed `);
      return navigate("/customer");
    } catch (error) {
      console.log(error.message);
    }
  };

  const [visible, setVisible] = useState(false);
  const handleVisibleChange = (newVisible) => setVisible(newVisible);

  useEffect(() => {
    dispatch(loadSingleCustomer(id));
  }, [id]);

  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <div className="customer-detail-page">
      <div className="customer-detail-card-wrap">
        {customer ? (
          <Fragment key={customer.id}>
            <Card bordered={false} className="customer-detail-card">
              <div className="customer-detail-header">
                <h5 className="customer-detail-title">
                  <i className="bi bi-person-lines-fill" aria-hidden />
                  <span className="customer-detail-name">{customer.name}</span>
                </h5>
                <div className="customer-detail-actions">
                  <Link
                    to={`/customer/${customer.id}/update`}
                    state={{ data: customer }}>
                    <Button type="primary" shape="round" icon={<EditOutlined />} aria-label="Edit customer" />
                  </Link>
                  <Popover
                    content={
                      <a onClick={onDelete}>
                        <Button type="primary" danger>Yes Please !</Button>
                      </a>
                    }
                    title="Are you sure you want to delete ?"
                    trigger="click"
                    visible={visible}
                    onVisibleChange={handleVisibleChange}>
                    <Button type="danger" shape="round" icon={<DeleteOutlined />} aria-label="Delete customer" />
                  </Popover>
                  {customer?.saleInvoice && (
                    <>
                      <Dropdown
                        overlay={<Menu onClick={colVisibilityClickHandler} items={columnItems} />}
                        placement="bottomRight">
                        <Button size="small" className="column-visibility">Column Visibility</Button>
                      </Dropdown>
                      <CSVLink
                        data={customer.saleInvoice}
                        className="btn btn-dark btn-sm"
                        filename={`customer_${customer.name}`}>
                        Download CSV
                      </CSVLink>
                    </>
                  )}
                </div>
              </div>
              <div className="customer-detail-info">
                <p>
                  <Typography.Text strong>Phone Number :</Typography.Text> {customer.phone ?? "N/A"}
                </p>
                <p>
                  <Typography.Text strong>Address :</Typography.Text> {customer.address ?? "—"}
                </p>
                <p>
                  <Typography.Text strong>Due Amount :</Typography.Text> {customer.due_amount ?? "0"}
                </p>
                <p>
                  <Typography.Text strong>Opening Balance :</Typography.Text> {customer.opening_balance ?? "0"}
                </p>
                <p>
                  <Typography.Text strong>Opening Balance Date :</Typography.Text>{" "}
                  {customer.opening_balance_date ? new Date(customer.opening_balance_date).toLocaleDateString() : "—"}
                </p>
              </div>
              <div className="customer-detail-section">
                <h5 className="customer-detail-section-heading">Invoices</h5>
                <CustomerInvoiceList
                  list={customer?.saleInvoice}
                  linkTo="/sale"
                  embedded
                  columns={columnsToShow.length ? columnsToShow : undefined}
                  scroll={{ x: true, y: TABLE_SCROLL_Y }}
                />
              </div>
              <CustomerReturnInvoiceList list={customer?.allReturnSaleInvoice} />
              <CustomerTransactionList list={customer?.allTransaction} />
            </Card>
          </Fragment>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
};

export default DetailCust;
